import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileQuestion, ArrowRight } from 'lucide-react';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { blogArticles } from '@/data/blogArticles';

/**
 * Page 404.
 *
 * Remplace l'ancien `<Navigate to="/" replace />` du catch-all de App.js.
 * Une redirection systématique vers l'accueil empêchait Google de recevoir
 * un vrai signal 404 : toute URL erronée restait indéfiniment dans l'index
 * sous le motif « Page avec redirection ».
 *
 * Bonus : si le chemin demandé correspond au slug d'un article publié mais
 * sans le préfixe /blog/ (erreur fréquente lors des demandes d'indexation
 * manuelles dans la Search Console), on propose directement la bonne URL.
 */
const NotFoundPage = () => {
  const location = useLocation();
  const requested = location.pathname.replace(/^\/+|\/+$/g, '');

  // Correspondance exacte avec un slug d'article (ex: /courrier-coface-que-faire)
  const exact = blogArticles.find((a) => a.slug === requested);

  // À défaut, suggestion par mots-clés communs avec le chemin demandé
  const fuzzy =
    !exact && requested.length > 4
      ? blogArticles
          .map((a) => {
            const tokens = requested.split(/[-_/]+/).filter((t) => t.length > 3);
            const score = tokens.filter((t) => a.slug.includes(t)).length;
            return { article: a, score };
          })
          .filter((r) => r.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, 3)
          .map((r) => r.article)
      : [];

  const suggestions = exact ? [exact] : fuzzy;

  return (
    <>
      <Helmet>
        <title>Page introuvable (404) | Anti-Recouvrement</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="Cette page n'existe pas ou a été déplacée." />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
        <Header />

        <section className="py-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <FileQuestion className="w-16 h-16 mx-auto mb-6" style={{ color: '#1e3a8a' }} />

            <h1 className="text-4xl font-extrabold mb-4" style={{ color: '#1e3a8a' }}>
              Cette page n'existe pas
            </h1>

            <p className="text-lg text-gray-700 leading-relaxed mb-10">
              L'adresse demandée est introuvable. Elle a peut-être été déplacée,
              ou le lien que vous avez suivi comporte une erreur.
            </p>

            {suggestions.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-10 text-left">
                <p className="font-semibold text-gray-900 mb-4">
                  {exact
                    ? 'Vous cherchiez sans doute cet article :'
                    : 'Ces articles correspondent peut-être à votre recherche :'}
                </p>
                <ul className="space-y-3">
                  {suggestions.map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/blog/${a.slug}`}
                        className="flex items-start gap-2 text-blue-700 hover:underline"
                      >
                        <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" />
                        <span>{a.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#upload">
                <Button size="lg" className="w-full sm:w-auto">
                  Analyser ma lettre gratuitement
                </Button>
              </Link>
              <Link to="/blog">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Consulter le blog
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default NotFoundPage;
